import { Clipboard } from '@angular/cdk/clipboard';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, computed, effect, ElementRef, inject, Inject, OnDestroy, OnInit, Optional, signal, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { EnvService } from 'src/app/env.service';
import { MediaDevicePermissionWatcherService } from 'src/app/media-device-permission-watcher.service';
import { InputComponent } from 'src/app/shared/components/ui/input/input.component';
import { SelectComponent, SelectOption } from 'src/app/shared/components/ui/select/select.component';
import { ChimeCallService } from 'src/app/shared/components/video-call-drawer-chime/chime-call.service';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { CustomDomainService, CustomDomainStatusResponse } from 'src/app/shared/services/custom-domain/custom-domain.service';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { TenantBrandingService } from 'src/app/shared/services/tenant-branding/tenant-branding.service';
import { TenantService } from 'src/app/shared/services/tenant/tenant.service';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { environment } from 'src/environments/environment';
import { MaterialModule } from '../../../shared/material.module';
import { ToastService } from '../../../shared/services/toast/toast.service';

interface AiAccessUser {
  _id: string;
  uid: string;
  fullName: string;
  email: string;
  photoURL?: SafeResourceUrl;
}

@Component({
  selector: 'app-global-settings',
  templateUrl: './global-settings.component.html',
  styleUrls: ['./global-settings.component.scss'],
  standalone: true,
  imports: [MaterialModule, TranslateModule, FormsModule, SelectComponent, InputComponent],
})
export class GlobalSettingsComponent implements OnInit, OnDestroy {
  public sidenavService = inject(SidenavService);
  private envService = inject(EnvService);
  public tenantBrandingService = inject(TenantBrandingService);
  public customDomainService = inject(CustomDomainService);
  public confirmMenuService = inject(ConfirmMenuService);
  public titleService = inject(Title);
  public clipboard = inject(Clipboard);
  public isSmallScreen = this.sidenavService.isSmallOrTablet;

  // Custom Domain State
  customDomainInput = '';
  customDomainData: CustomDomainStatusResponse | null = null;
  isLoadingDomain = false;
  isSavingDomain = false;
  isDeletingDomain = false;
  isApplyingCert = false;
  defaultCnameTarget = 'domains-dev.skillztank.com';

  loading = false;
  aiTTSModels = [
    {
      name: 'Google',
      value: 'google',
    },
    {
      name: 'Gemini 3.1 Flash',
      value: 'gemini',
    },
  ];
  sanitizer = inject(DomSanitizer);
  selectedTTSModels = { en: 'gemini', fr: 'gemini' };
  transcriptionModelSubscription: Subscription;
  audioConfiguration;
  searchAI;
  searchAIs = [
    {
      value: 'gemini-3-flash-preview',
      name: 'Search.AISearchGeminiFlash',
    },
    {
      value: 'gemini-3.1-pro-preview',
      name: 'Search.AISearchGeminiPro',
    },
  ];
  audioTypes: any = {
    google: {
      en: [
        { value: 'en-US-Chirp3-HD-Achernar', view: 'Achernar', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Achird', view: 'Achird', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Algenib', view: 'Algenib', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Algieba', view: 'Algieba', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Alnilam', view: 'Alnilam', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Aoede', view: 'Aoede', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Autonoe', view: 'Autonoe', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Callirrhoe', view: 'Callirrhoe', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Charon', view: 'Charon', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Despina', view: 'Despina', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Enceladus', view: 'Enceladus', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Erinome', view: 'Erinome', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Fenrir', view: 'Fenrir', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Gacrux', view: 'Gacrux', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Iapetus', view: 'Iapetus', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Kore', view: 'Kore', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Laomedeia', view: 'Laomedeia', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Leda', view: 'Leda', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Orus', view: 'Orus', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Puck', view: 'Puck', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Pulcherrima', view: 'Pulcherrima', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Rasalgethi', view: 'Rasalgethi', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Sadachbia', view: 'Sadachbia', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Sadaltager', view: 'Sadaltager', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Schedar', view: 'Schedar', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Sulafat', view: 'Sulafat', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Umbriel', view: 'Umbriel', gender: 'Global.Male' },
        { value: 'en-US-Chirp3-HD-Vindemiatrix', view: 'Vindemiatrix', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Zephyr', view: 'Zephyr', gender: 'Global.Female' },
        { value: 'en-US-Chirp3-HD-Zubenelgenubi', view: 'Zubenelgenubi', gender: 'Global.Male' },
      ],
      fr: [
        { value: 'fr-FR-Chirp3-HD-Achernar', view: 'Achernar', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Achird', view: 'Achird', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Algenib', view: 'Algenib', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Algieba', view: 'Algieba', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Alnilam', view: 'Alnilam', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Aoede', view: 'Aoede', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Autonoe', view: 'Autonoe', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Callirrhoe', view: 'Callirrhoe', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Charon', view: 'Charon', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Despina', view: 'Despina', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Enceladus', view: 'Enceladus', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Erinome', view: 'Erinome', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Fenrir', view: 'Fenrir', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Gacrux', view: 'Gacrux', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Iapetus', view: 'Iapetus', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Kore', view: 'Kore', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Laomedeia', view: 'Laomedeia', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Leda', view: 'Leda', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Orus', view: 'Orus', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Puck', view: 'Puck', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Pulcherrima', view: 'Pulcherrima', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Rasalgethi', view: 'Rasalgethi', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Sadachbia', view: 'Sadachbia', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Sadaltager', view: 'Sadaltager', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Schedar', view: 'Schedar', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Sulafat', view: 'Sulafat', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Umbriel', view: 'Umbriel', gender: 'Global.Male' },
        { value: 'fr-FR-Chirp3-HD-Vindemiatrix', view: 'Vindemiatrix', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Zephyr', view: 'Zephyr', gender: 'Global.Female' },
        { value: 'fr-FR-Chirp3-HD-Zubenelgenubi', view: 'Zubenelgenubi', gender: 'Global.Male' },
      ],
    },
  };

  ttsLanguages: SelectOption[] = [
    {
      value: 'en',
      label: 'Home.Header.English',
    },
    { value: 'fr', label: 'Home.Header.French' },
  ];
  selectedMicrophone;
  chunkCreatorOptions = [];
  chunkCreatorData = {};
  selectedTTSLanguage = 'fr';
  microphones = [];
  cameras = [];
  sampleText: string;
  isGeneratingSample = false;
  isPlayingSample = false;
  private currentAudio: HTMLAudioElement;

  @ViewChild('brandingFileInput') brandingFileInput!: ElementRef<HTMLInputElement>;
  readonly brandingPresetColors = ['#5a73c5', '#2563eb', '#0d9488', '#7c3aed', '#e11d48', '#d97706', '#1e293b'];
  readonly brandingDarkPreview = signal(false);
  isSavingBranding = false;
  readonly brandingSelectedFile = signal<File | null>(null);
  readonly brandingSelectedColor = signal<string>(this.tenantBrandingService.primaryColor() || '#5a73c5');

  readonly brandingPreviewLogoUrl = computed(() => {
    const file = this.brandingSelectedFile();
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      return this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
    }
    const currentUrl = this.tenantBrandingService.logoUrl();
    if (currentUrl && currentUrl.startsWith('blob:')) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(currentUrl);
    }
    return currentUrl;
  });

  onBrandingPreviewLogoError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/creatives/logo-w70.png';
  }

  toggleBrandingDarkPreview(): void {
    this.brandingDarkPreview.update((prev) => !prev);
  }

  selectBrandingColor(color: string): void {
    this.brandingSelectedColor.set(color);
    this.tenantBrandingService.applyPrimaryColor(color);
  }

  onBrandingCustomColorInput(event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.selectBrandingColor(val);
  }

  onBrandingHexTextChange(event: Event): void {
    const val = (event.target as HTMLInputElement).value.trim();
    if (/^#([0-9A-F]{3}){1,2}$/i.test(val)) {
      this.selectBrandingColor(val);
    }
  }

  onBrandingFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.handleBrandingFile(input.files[0]);
    }
  }

  onBrandingFileDropped(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
      this.handleBrandingFile(event.dataTransfer.files[0]);
    }
  }

  private handleBrandingFile(file: File): void {
    if (file.size > 2 * 1024 * 1024) {
      this.toast.snackbar.open('La taille du fichier dépasse la limite de 2 Mo', 'Ok', { duration: 3000 });
      return;
    }
    const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      this.toast.snackbar.open('Seuls les formats SVG, PNG et JPG sont autorisés', 'Ok', { duration: 3000 });
      return;
    }
    this.brandingSelectedFile.set(file);
  }

  async saveBrandingSettings(): Promise<void> {
    this.isSavingBranding = true;
    try {
      const file = this.brandingSelectedFile();
      if (file) {
        await this.tenantBrandingService.uploadLogo(file).toPromise();
      }

      await this.tenantBrandingService.updateSettings(this.brandingSelectedColor()).toPromise();

      this.brandingSelectedFile.set(null);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 3000 });
    } catch (err: any) {
      console.error('[GlobalSettings] Save branding error:', err);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 3000 });
    } finally {
      this.isSavingBranding = false;
    }
  }

  async deleteBrandingLogo(): Promise<void> {
    this.isSavingBranding = true;
    try {
      await this.tenantBrandingService.deleteLogo().toPromise();
      this.brandingSelectedFile.set(null);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 3000 });
    } catch (err: any) {
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 3000 });
    } finally {
      this.isSavingBranding = false;
    }
  }

  async resetBrandingSettings(event: Event): Promise<void> {
    const trigger = event.currentTarget as HTMLElement;
    this.confirmMenuService.confirm(trigger, 'Global.Reset', null, '#ef4444', null, 'MySpace.Branding.ConfirmReset').subscribe(async () => {
      this.isSavingBranding = true;
      try {
        await this.tenantBrandingService.deleteLogo().toPromise();
        await this.tenantBrandingService.updateSettings(null).toPromise();
        this.brandingSelectedColor.set('#5a73c5');
        this.brandingSelectedFile.set(null);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 3000 });
      } catch (err: any) {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 3000 });
      } finally {
        this.isSavingBranding = false;
      }
    });
  }

  appName: string = '';
  workspaceSlug: string = '';
  initialWorkspaceSlug: string = '';
  subdomain: string = '';
  initialSubdomain: string = '';
  isSavingAppName = false;
  isSavingSlug = false;
  slugValidationError: string | null = null;

  aiAccessMode: 'all' | 'restricted' = 'all';
  aiAllowedUserIds: string[] = [];
  aiAccessUsers: AiAccessUser[] = [];
  aiUserSearchInput: string = '';
  aiUserSearchResults: AiAccessUser[] = [];
  isSearchingAiUsers = false;
  private tenantService = inject(TenantService);
  @ViewChild('aiUserSearchInputRef') aiUserSearchInputEl!: ElementRef<HTMLInputElement>;
  @ViewChild('aiUserResultsTemplate') aiUserResultsTemplate!: TemplateRef<any>;
  private aiUserOverlayRef: OverlayRef | null = null;

  aiAccessModeOptions: SelectOption[] = [
    { value: 'all', label: 'MySpace.AiAccess.ModeAll' },
    { value: 'restricted', label: 'MySpace.AiAccess.ModeRestricted' },
  ];

  private readonly defaultSampleTexts = {
    en: 'Hello, you can write here the text you want to use to preview the voice.',
    fr: 'Bonjour, vous pouvez écrire ici le texte que vous souhaitez utiliser pour prévisualiser la voix.',
  };

  // PSTN Greeting Properties
  pstnLanguages: SelectOption[] = [
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'en-AU', label: 'English (Australia)' },
    { value: 'fr-FR', label: 'Français (France)' },
    { value: 'fr-CA', label: 'Français (Canada)' },
    { value: 'es-ES', label: 'Español (España)' },
    { value: 'es-MX', label: 'Español (México)' },
  ];
  pstnGreetingText: string = '';
  pstnGreetingLang: string = 'fr-CA';
  isGeneratingPstnPreview = false;
  isPlayingPstnPreview = false;
  pstnAudioBlob: Blob | null = null;
  private pstnAudio: HTMLAudioElement;

  get currentVoiceOptions(): SelectOption[] {
    const provider = this.selectedTTSModels?.[this.selectedTTSLanguage];
    if (!provider || !this.audioTypes[provider]?.[this.selectedTTSLanguage]) return [];

    return this.audioTypes[provider][this.selectedTTSLanguage].map((audioType) => ({
      value: audioType.value,
      label: audioType.gender ? `${audioType.view} (${this.translate.instant(audioType.gender)})` : audioType.view,
    }));
  }

  get domainSuffix(): string {
    return environment.domain;
  }

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) public bottomSheetData: any,
    @Optional() public dialogRef: MatDialogRef<GlobalSettingsComponent>,
    public authService: AuthService,
    private toast: ToastService,
    public translate: TranslateService,
    private permissionService: MediaDevicePermissionWatcherService,
    private chunkService: ChunkService,
    private chimeCallService: ChimeCallService,
    private dialog: MatDialog,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {
    if (!this.dialogData && !this.bottomSheetData) {
      effect(() => {
        const isSmall = this.isSmallScreen();
        this.sidenavService.showGlobalToggle.set(!isSmall);
      });
    }

    this.selectedMicrophone = localStorage.getItem('selectedMicrophone');
    this.audioTypes.gemini = this.audioTypes.google;
    this.transcriptionModelSubscription = this.authService.userConfiguration$.subscribe((res) => {
      if (res.searchAI) {
        this.searchAI = res.searchAI;
        // Enforce Gemini
        if (this.searchAI.startsWith('gpt')) {
          this.searchAI = 'gemini-3-flash-preview';
        }
      }
      if (res.TTSAIModel) {
        this.selectedTTSModels = res.TTSAIModel;
        Object.keys(this.selectedTTSModels).forEach((key) => {
          if (!this.selectedTTSModels[key] || this.selectedTTSModels[key] === 'google') {
            this.selectedTTSModels[key] = 'gemini';
          }
        });
      }
      if (res.audioConfiguration) {
        this.audioConfiguration = res.audioConfiguration;
      }
      if (res.chunkCreator) {
        this.chunkCreatorData = res.chunkCreator;
        this.chunkCreatorOptions = [];
        for (const option in res.chunkCreator) {
          if (option != 'generateAiAfterTranscription') {
            this.chunkCreatorOptions.push({
              name: option,
              value: res.chunkCreator[option],
            });
          }
        }
      }
    });
    this.sampleText = this.defaultSampleTexts[this.selectedTTSLanguage];
  }

  ngOnInit(): void {
    this.getMediaDevices();
    this.tenantService.getTenantSettings().subscribe({
      next: (res) => {
        if (res.ok) {
          if (res.appname) this.appName = res.appname;
          if (res.workspaceSlug) {
            this.workspaceSlug = res.workspaceSlug;
            this.initialWorkspaceSlug = res.workspaceSlug;
          }
          if (res.subdomain) {
            this.subdomain = res.subdomain;
            this.initialSubdomain = res.subdomain;
          }
          if (res.customPstnGreetingText) this.pstnGreetingText = res.customPstnGreetingText;
          if (res.customPstnGreetingLang) this.pstnGreetingLang = res.customPstnGreetingLang;
          if (res.aiAccessMode) this.aiAccessMode = res.aiAccessMode;
          if (res.aiAllowedUsers && Array.isArray(res.aiAllowedUsers)) {
            this.aiAllowedUserIds = res.aiAllowedUsers;
            this.loadAiAccessUserDetails();
          }
          this.tenantService.setTenantAiSettings({
            aiAllowedUsers: res.aiAllowedUsers || null,
            aiAccessMode: res.aiAccessMode || 'all',
          });
        }
      },
      error: (err) => console.error('Error fetching tenant settings:', err),
    });

    if (this.authService.isAdmin()) {
      this.loadCustomDomainInfo();
    }
  }

  getMediaDevices() {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      this.microphones = devices.filter((device) => device.kind === 'audioinput' && device.deviceId.length);
      this.cameras = devices.filter((device) => device.kind === 'videoinput' && device.deviceId.length);
    });
  }

  areNotificationsPermitted() {
    return Notification.permission == 'granted';
  }

  async allowNotifications() {
    // FCM Disabled - No longer requesting permission
    // await Notification.requestPermission();
  }

  getCurrentLanguage() {
    return this.translate.currentLang;
  }

  loadCustomDomainInfo() {
    this.isLoadingDomain = true;
    this.customDomainService.getCustomDomain().subscribe({
      next: (res) => {
        this.isLoadingDomain = false;
        this.customDomainData = res;
        if (res.customDomain) {
          this.customDomainInput = res.customDomain;
        }
      },
      error: (err) => {
        this.isLoadingDomain = false;
        console.error('Error loading custom domain status:', err);
      },
    });
  }

  bindCustomDomain() {
    if (!this.customDomainInput || this.isSavingDomain) return;
    this.isSavingDomain = true;
    this.customDomainService.bindCustomDomain(this.customDomainInput).subscribe({
      next: (res) => {
        this.isSavingDomain = false;
        this.toast.snackbar.open(this.translate.instant('MySpace.CustomDomain.BindSuccess'), 'Ok', { duration: 3000 });
        this.loadCustomDomainInfo();
      },
      error: (err) => {
        this.isSavingDomain = false;
        const msg = err.error?.error || this.translate.instant('MySpace.CustomDomain.BindError');
        this.toast.snackbar.open(msg, 'Ok', { duration: 3000 });
      },
    });
  }

  applyCertificate() {
    if (this.isApplyingCert) return;
    this.isApplyingCert = true;
    this.customDomainService.applyCertificate().subscribe({
      next: (res) => {
        this.isApplyingCert = false;
        this.toast.snackbar.open(this.translate.instant('MySpace.CustomDomain.ApplySuccess'), 'Ok', { duration: 3000 });
        this.loadCustomDomainInfo();
      },
      error: (err) => {
        this.isApplyingCert = false;
        const msg = err.error?.error || this.translate.instant('MySpace.CustomDomain.ApplyError');
        this.toast.snackbar.open(msg, 'Ok', { duration: 3000 });
      },
    });
  }

  removeCustomDomain(event: Event) {
    const trigger = event.currentTarget as HTMLElement;
    this.confirmMenuService.confirm(trigger, 'Global.Confirm', null, 'warn', null, 'MySpace.CustomDomain.ConfirmRemove').subscribe(() => {
      this.isDeletingDomain = true;
      this.customDomainService.unbindCustomDomain().subscribe({
        next: () => {
          this.isDeletingDomain = false;
          this.customDomainInput = '';
          this.toast.snackbar.open(this.translate.instant('MySpace.CustomDomain.RemoveSuccess'), 'Ok', { duration: 3000 });
          this.loadCustomDomainInfo();
        },
        error: (err) => {
          this.isDeletingDomain = false;
          this.toast.snackbar.open(err.error?.error || 'Failed to remove domain', 'Ok', { duration: 3000 });
        },
      });
    });
  }

  copyCnameTarget() {
    const target = this.customDomainData?.cnameTarget || this.defaultCnameTarget;
    if (target) {
      this.clipboard.copy(target);
      this.toast.snackbar.open(this.translate.instant('Global.CopiedToClipboard'), 'Ok', { duration: 3000 });
    }
  }

  saveSettings() {
    this.slugValidationError = null;

    if (this.authService.isAdmin() && this.workspaceSlug) {
      const currentTenantId = this.tenantBrandingService.tenantConfig()?.tenantId;
      const slugLower = this.workspaceSlug.trim().toLowerCase();
      if (slugLower.startsWith('t-') && slugLower !== currentTenantId?.toLowerCase()) {
        this.slugValidationError = 'MySpace.WorkspaceSlugInvalidPrefix';
        return;
      }
    }

    this.loading = true;
    localStorage.setItem('selectedMicrophone', this.selectedMicrophone);

    const promises = [];

    if (this.authService.isWriter()) {
      promises.push(
        this.authService.setUserConfigurationDocument({
          TTSAIModel: this.selectedTTSModels,
          audioConfiguration: this.audioConfiguration,
          searchAI: this.searchAI,
          chunkCreator: this.chunkCreatorData,
        })
      );
    }

    if (this.authService.isAdmin() && this.appName && this.appName.trim() !== '') {
      promises.push(
        new Promise((resolve, reject) => {
          this.tenantService.updateTenantAppname(this.appName.trim(), this.workspaceSlug.trim()).subscribe({
            next: (res) => {
              if (this.appName.trim()) {
                this.titleService.setTitle(this.appName.trim());
                document.title = this.appName.trim();
              }
              if (res.workspaceSlug) {
                this.initialWorkspaceSlug = res.workspaceSlug;
                this.initialSubdomain = `${res.workspaceSlug}.${this.domainSuffix}`;
              }
              resolve(res);
            },
            error: (err) => {
              this.slugValidationError = err.error?.error || 'Failed to save slug';
              reject(err);
            },
          });
        })
      );
    }

    if (this.authService.isAdmin()) {
      promises.push(
        new Promise((resolve, reject) => {
          this.tenantService.updateTenantAiAccess(this.aiAccessMode === 'restricted' ? this.aiAllowedUserIds : null, this.aiAccessMode).subscribe({
            next: (res: any) => {
              this.tenantService.setTenantAiSettings({
                aiAllowedUsers: res.aiAllowedUsers || null,
                aiAccessMode: res.aiAccessMode || 'all',
              });
              resolve(res);
            },
            error: reject,
          });
        })
      );
    }

    Promise.all(promises)
      .then(() => {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', {
          duration: 2000,
        });
        this.close();
      })
      .catch((err) => {
        console.error(err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
          duration: 2000,
        });
      })
      .finally(() => {
        this.loading = false;
      });
  }

  getOptionViewValue(option) {
    return this.translate.instant('Courses.ChunkCreator.' + option);
  }

  close() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  get isDefaultSlug(): boolean {
    const currentTenantId = this.tenantBrandingService.tenantConfig()?.tenantId;
    return this.workspaceSlug === currentTenantId;
  }

  get isSubdomainActive(): boolean {
    return !!this.initialSubdomain;
  }

  get subdomainLink(): string {
    return `https://${this.workspaceSlug}.${this.domainSuffix}`;
  }

  activateSlugField() {
    this.saveWorkspaceSlug();
  }

  saveWorkspaceSlug() {
    this.slugValidationError = null;
    if (!this.workspaceSlug || this.workspaceSlug.trim() === '') return;

    const currentTenantId = this.tenantBrandingService.tenantConfig()?.tenantId;
    const slugLower = this.workspaceSlug.trim().toLowerCase();
    if (slugLower.startsWith('t-') && slugLower !== currentTenantId?.toLowerCase()) {
      this.slugValidationError = 'MySpace.WorkspaceSlugInvalidPrefix';
      return;
    }

    this.isSavingSlug = true;
    this.tenantService.updateTenantAppname(this.appName.trim(), this.workspaceSlug.trim()).subscribe({
      next: (res) => {
        this.isSavingSlug = false;
        this.initialWorkspaceSlug = res.workspaceSlug || this.workspaceSlug.trim();
        this.initialSubdomain = `${this.initialWorkspaceSlug}.${this.domainSuffix}`;
        this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 2000 });
      },
      error: (err) => {
        this.isSavingSlug = false;
        this.slugValidationError = err.error?.error || 'Failed to save slug';
      },
    });
  }

  showPermissionDemo() {
    this.toast.showDevicePermissionError();
  }

  async grantAccess(type) {
    let success = false;
    if (type == 'camera') {
      success = await this.permissionService.requestCameraAccess();
    } else if (type == 'microphone') {
      success = await this.permissionService.requestMicrophoneAccess();
    }

    if (!success) {
      this.dialog.open(PermissionErrorDialogComponent, { width: '500px' });
    }

    this.getMediaDevices();
  }
  onTTSLanguageChange(res: any) {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.isPlayingSample = false;
    }
    if (this.audioConfiguration?.[this.selectedTTSLanguage]) {
      const provider = this.selectedTTSModels[this.selectedTTSLanguage];
      const newVoice = this.audioTypes[provider][this.selectedTTSLanguage][0].value;
      this.audioConfiguration[this.selectedTTSLanguage].voiceType = newVoice;
    }
    this.sampleText = this.defaultSampleTexts[this.selectedTTSLanguage];
  }

  onSliderUpdate(event, type) {
    this.audioConfiguration[this.selectedTTSLanguage][type] = event.value;
  }

  resetSlider(type) {
    const defaultValues = this.authService.getDefaultSliderValues();
    this.audioConfiguration[this.selectedTTSLanguage][type] = defaultValues[type];
  }

  generateOrStopSample() {
    if (this.isPlayingSample && this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.isPlayingSample = false;
      return;
    }

    this.isGeneratingSample = true;
    const lang = this.selectedTTSLanguage;
    const langCode = lang === 'en' ? 'en-US' : 'fr-FR';
    const audioConfig = this.audioConfiguration[lang];
    const provider = this.selectedTTSModels[lang];

    this.chunkService.generateSpeechSample({ text: this.sampleText, languageCode: langCode, audioConfig, provider }).subscribe({
      next: (blob) => {
        const audioUrl = URL.createObjectURL(blob);
        this.currentAudio = new Audio(audioUrl);
        this.currentAudio.play();
        this.isPlayingSample = true;
        this.isGeneratingSample = false;

        this.currentAudio.onended = () => {
          this.isPlayingSample = false;
        };
        this.currentAudio.onpause = () => {
          this.isPlayingSample = false;
        };
      },
      error: (err) => {
        console.error(err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
        this.isGeneratingSample = false;
      },
    });
  }

  generateOrStopPstnPreview() {
    if (this.isPlayingPstnPreview && this.pstnAudio) {
      this.pstnAudio.pause();
      this.pstnAudio.currentTime = 0;
      this.isPlayingPstnPreview = false;
      return;
    }

    if (!this.pstnGreetingText || !this.pstnGreetingText.trim()) {
      this.toast.snackbar.open(this.translate.instant('Errors.MissingText'), 'Ok', { duration: 2000 });
      return;
    }

    this.isGeneratingPstnPreview = true;
    this.chimeCallService.previewPstnGreeting(this.pstnGreetingText, this.pstnGreetingLang).subscribe({
      next: (blob) => {
        this.pstnAudioBlob = blob;
        const audioUrl = URL.createObjectURL(blob);
        this.pstnAudio = new Audio(audioUrl);
        this.pstnAudio.play();
        this.isPlayingPstnPreview = true;
        this.isGeneratingPstnPreview = false;

        this.pstnAudio.onended = () => {
          this.isPlayingPstnPreview = false;
        };
        this.pstnAudio.onpause = () => {
          this.isPlayingPstnPreview = false;
        };
      },
      error: (err) => {
        console.error(err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
        this.isGeneratingPstnPreview = false;
      },
    });
  }

  downloadPstnPreview() {
    if (!this.pstnAudioBlob) return;
    const url = URL.createObjectURL(this.pstnAudioBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pstn-greeting.wav';
    a.click();
    URL.revokeObjectURL(url);
  }

  async uploadPstnGreeting() {
    if (!this.pstnAudioBlob) return;

    this.loading = true;

    try {
      const reader = new FileReader();
      reader.readAsDataURL(this.pstnAudioBlob);
      reader.onloadend = async () => {
        const base64data = (reader.result as string).split(',')[1];

        this.chimeCallService.uploadPstnGreeting(this.pstnGreetingText, this.pstnGreetingLang, base64data, this.pstnAudioBlob.type).subscribe({
          next: () => {
            this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 2000 });
            this.loading = false;
          },
          error: (err) => {
            console.error('Error uploading PSTN greeting', err);
            this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
            this.loading = false;
          },
        });
      };
    } catch (err) {
      console.error(err);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
      this.loading = false;
    }
  }

  loadAiAccessUserDetails() {
    if (this.aiAllowedUserIds.length === 0) {
      this.aiAccessUsers = [];
      return;
    }
    this.aiAllowedUserIds.forEach((uid) => {
      if (!this.aiAccessUsers.find((u) => u.uid === uid)) {
        this.authService.searchUserSimple(uid).subscribe({
          next: (res: any) => {
            if (res && res.length > 0) {
              const user = res[0];
              console.log(user);
              this.aiAccessUsers.push({
                _id: user._id || user.id,
                uid: uid,
                fullName: (user.fullName || `${user.firstName || ''} ${user.lastName || ''}`).trim(),
                email: user.email,
                photoURL: this.sanitizer.bypassSecurityTrustResourceUrl(user.photoURL),
              });
            }
          },
          error: () => {},
        });
      }
    });
  }

  openAiUserOverlay() {
    if (this.aiUserOverlayRef) return;
    if (!this.aiUserSearchInputEl) return;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.aiUserSearchInputEl)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
      ])
      .withViewportMargin(8);

    this.aiUserOverlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      width: this.aiUserSearchInputEl.nativeElement.offsetWidth,
    });

    const portal = new TemplatePortal(this.aiUserResultsTemplate, this.viewContainerRef);
    this.aiUserOverlayRef.attach(portal);

    this.aiUserOverlayRef.backdropClick().subscribe(() => {
      this.closeAiUserOverlay();
    });
  }

  private closeAiUserOverlay() {
    if (this.aiUserOverlayRef) {
      this.aiUserOverlayRef.dispose();
      this.aiUserOverlayRef = null;
    }
  }

  searchAiUsers() {
    const query = this.aiUserSearchInput.trim();
    if (query.length < 2) {
      this.aiUserSearchResults = [];
      this.closeAiUserOverlay();
      return;
    }
    this.isSearchingAiUsers = true;
    this.authService.searchUserSimple(query).subscribe({
      next: (res: any) => {
        this.aiUserSearchResults = (res || [])
          .filter((u: any) => !this.aiAllowedUserIds.includes(u.uid))
          .map((u: any) => ({
            _id: u._id || u.id,
            fullName: (u.name || u.fullName || `${u.firstName || ''} ${u.lastName || ''}`).trim(),
            email: u.email,
            uid: u.id,
            photoURL: u.photoURL,
          }));
        this.isSearchingAiUsers = false;
        if (this.aiUserSearchResults.length > 0) {
          this.openAiUserOverlay();
        } else {
          this.closeAiUserOverlay();
        }
      },
      error: () => {
        this.isSearchingAiUsers = false;
        this.closeAiUserOverlay();
      },
    });
  }

  addAiAccessUser(user: AiAccessUser) {
    console.log(user, this.aiAllowedUserIds);
    if (!this.aiAllowedUserIds.includes(user.uid)) {
      this.aiAllowedUserIds.push(user.uid);
      this.aiAccessUsers.push(user);
    }
    this.aiUserSearchInput = '';
    this.aiUserSearchResults = [];
    this.closeAiUserOverlay();
  }

  removeAiAccessUser(userId: string) {
    this.aiAllowedUserIds = this.aiAllowedUserIds.filter((id) => id !== userId);
    this.aiAccessUsers = this.aiAccessUsers.filter((u) => u.uid !== userId);
  }

  async handleToggle(event: MatSlideToggleChange, type: string): Promise<void> {
    if (event.checked) {
      switch (type) {
        case 'notifications':
          this.allowNotifications();
          break;
        case 'microphone':
          await this.grantAccess('microphone');
          break;
        case 'camera':
          await this.grantAccess('camera');
          break;
        default:
          break;
      }
    }
  }

  ngOnDestroy(): void {
    if (!this.dialogData && !this.bottomSheetData) {
      this.sidenavService.showGlobalToggle.set(true);
    }
    this.transcriptionModelSubscription.unsubscribe();
    this.closeAiUserOverlay();
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    if (this.pstnAudio) {
      this.pstnAudio.pause();
      this.pstnAudio = null;
    }
  }
}

@Component({
  selector: 'app-permission-error-dialog',
  template: `
    <div class="standard-dialog">
      <div class="header" mat-dialog-title>
        <div class="title-section">
          <h2>{{ 'Permissions.BrowserSettingsTitle' | translate }}</h2>
          <p class="subtitle text-[13px] text-gray-500 mt-1 leading-relaxed font-normal">
            {{ 'Permissions.BrowserSettingsSubtitle' | translate }}
          </p>
        </div>
        <button mat-icon-button (click)="dialogRef.close()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content class="form-content">
        <ol class="permission-guide__steps m-0 pl-5 list-decimal">
          <li class="text-sm text-gray-600 leading-loose mb-2">
            {{ 'Permissions.BrowserSettingsStep1' | translate }}
          </li>
          <li class="text-sm text-gray-600 leading-loose mb-2">
            {{ 'Permissions.BrowserSettingsStep2' | translate }}
          </li>
          <li class="text-sm text-gray-600 leading-loose">
            {{ 'Permissions.BrowserSettingsStep3' | translate }}
          </li>
        </ol>
      </mat-dialog-content>

      <mat-dialog-actions class="footer">
        <div class="right-actions">
          <button mat-flat-button color="primary" (click)="dialogRef.close()">Ok</button>
        </div>
      </mat-dialog-actions>
    </div>
  `,
  standalone: true,
  imports: [MaterialModule, TranslateModule],
})
export class PermissionErrorDialogComponent {
  constructor(public dialogRef: MatDialogRef<PermissionErrorDialogComponent>) {}
}
